import React, { useState, useEffect } from 'react';
import apiService from '../../services/ApiServices';
import {
  Container, Row, Col, Card, Button, Table, Form, Modal,
  Spinner, Alert
} from 'react-bootstrap';
import {
  FaPlus, FaEdit, FaTrash, FaArrowLeft, FaListUl, FaSave, FaTimes
} from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';

const AddQuestionType = () => {
  const navigate = useNavigate();
  const [questionTypes, setQuestionTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMsg, setSuccessMsg] = useState('');

  // Modal States
  const [showModal, setShowModal] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [currentId, setCurrentId] = useState(null);
  const [formData, setFormData] = useState({
    name: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiService.get('/api/question-type-masters');
      
      // Check response structure, handling both likely formats
      if (response?.success) {
        const data = response.message?.questionTypeMasters || response.message?.questionTypes || response.message?.data || response.message || [];
        // Ensure we're setting an array
        setQuestionTypes(Array.isArray(data) ? data : []);
      } else {
        setQuestionTypes([]);
      }
    } catch (err) {
      console.error(err);
      setError('Failed to load question types.');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      setError('Question type name is required.');
      return;
    }

    try {
      setError(null);
      const payload = {
        name: formData.name.trim()
      };

      let response;
      if (isEditMode && currentId) {
        response = await apiService.put(`/api/question-type-masters/${currentId}`, payload);
      } else {
        response = await apiService.post('/api/question-type-masters', payload);
      }

      if (response?.success) {
        setSuccessMsg(isEditMode ? 'Question Type updated successfully' : 'Question Type created successfully');
        setShowModal(false);
        fetchData();
      } else {
        setError(response?.message || 'Operation failed');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Error saving data');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this question type?')) return;
    
    try {
      const response = await apiService.delete(`/api/question-type-masters/${id}`);
      if (response?.success) {
        setSuccessMsg('Question Type deleted successfully');
        fetchData();
      } else {
        setError(response?.message || 'Failed to delete item');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Error deleting item');
    }
  };

  const openModal = (item = null) => {
    if (item) {
      setIsEditMode(true);
      // Assuming ID is likely id or question_type_master_id based on pattern
      setCurrentId(item.id || item.question_type_master_id || item._id); 
      setFormData({
        name: item.name || ''
      });
    } else {
      setIsEditMode(false);
      setCurrentId(null);
      setFormData({
        name: ''
      });
    }
    setShowModal(true);
  };

  return (
    <Container fluid className="py-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <Button variant="outline-secondary" className="mb-2" onClick={() => navigate(-1)}>
            <FaArrowLeft className="me-2" /> Back
          </Button>
          <h2 className="mb-0">
            <FaListUl className="me-2 text-primary" />
            Question Type Master
          </h2>
        </div>
        <Button variant="primary" onClick={() => openModal()}>
          <FaPlus className="me-2" /> Add Question Type
        </Button>
      </div>

      {error && <Alert variant="danger" dismissible onClose={() => setError(null)}>{error}</Alert>}
      {successMsg && <Alert variant="success" dismissible onClose={() => setSuccessMsg('')}>{successMsg}</Alert>}

      {loading ? (
        <div className="text-center py-5">
          <Spinner animation="border" variant="primary" />
        </div>
      ) : (
        <Card className="border-0 shadow-sm">
          <Card.Body>
            <Table responsive hover className="align-middle">
              <thead className="bg-light">
                <tr>
                  <th>#</th>
                  <th>Question Type Name</th>
                  <th className="text-end">Actions</th>
                </tr>
              </thead>
              <tbody>
                {questionTypes.length === 0 ? (
                  <tr><td colSpan="3" className="text-center py-4">No question types found.</td></tr>
                ) : (
                  questionTypes.map((item, index) => (
                    <tr key={item.id || item.question_type_master_id || index}>
                      <td>{index + 1}</td>
                      <td><strong>{item.name}</strong></td>
                      <td className="text-end">
                        <Button variant="outline-primary" size="sm" className="me-2" onClick={() => openModal(item)}>
                          <FaEdit />
                        </Button>
                        <Button variant="outline-danger" size="sm" onClick={() => handleDelete(item.id || item.question_type_master_id)}>
                          <FaTrash />
                        </Button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          </Card.Body>
        </Card>
      )}

      <Modal show={showModal} onHide={() => setShowModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>{isEditMode ? 'Edit Question Type' : 'Add Question Type'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Name *</Form.Label>
              <Form.Control
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g. Multiple Choice, Fill in the blanks"
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            <FaTimes className="me-2" /> Cancel
          </Button>
          <Button variant="primary" onClick={handleSave}>
            <FaSave className="me-2" />
            {isEditMode ? 'Update' : 'Create'}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default AddQuestionType;